import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripeConfig } from '../config/stripe';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CreditCard, Film } from 'lucide-react';
import BuyCreditsModal from '../components/BuyCreditsModal';
import './PaymentSettings.css';

// Initialize Stripe
const stripePromise = loadStripe(stripeConfig.publishableKey);

// Initialize Firebase Functions
const functions = getFunctions();
const applyCouponToSubscription = httpsCallable(functions, 'applyCouponToSubscription');
const removeCouponFromSubscription = httpsCallable(functions, 'removeCouponFromSubscription');

// Stripe Element Styles
const stripeElementStyle = {
  base: {
    fontSize: '15px',
    color: '#333',
    '::placeholder': {
      color: '#aaa',
    },
  },
  invalid: {
    color: '#dc3545',
  },
};

// Card Form Component (needs to be inside Elements provider)
function CardForm({ onSuccess, savedCard, setSavedCard, zipCode, setZipCode }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [focusedElement, setFocusedElement] = useState(null);
  const { currentUser } = useAuth();

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setProcessing(true);
    setCardError('');

    try {
      const cardNumber = elements.getElement(CardNumberElement);
      
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber,
        billing_details: {
          address: {
            postal_code: zipCode
          }
        }
      });

      if (error) {
        setCardError(error.message);
        setProcessing(false);
        return;
      }

      // Store card info in Firestore
      const cardData = {
        last4: paymentMethod.card.last4,
        brand: paymentMethod.card.brand,
        expMonth: paymentMethod.card.exp_month,
        expYear: paymentMethod.card.exp_year,
        paymentMethodId: paymentMethod.id
      };

      await updateDoc(doc(db, 'users', currentUser.uid), {
        savedCard: cardData,
        billingZip: zipCode,
        updatedAt: new Date()
      });
      
      setSavedCard(cardData);
      onSuccess('Payment method saved successfully');
    } catch (err) {
      setCardError('An error occurred. Please try again.');
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  if (savedCard) {
    return (
      <div className="saved-card">
        <div className="card-icon">
          <CreditCard size={16} />
        </div>
        <div className="card-details">
          <div className="card-number">•••• •••• •••• {savedCard.last4}</div>
          <div className="card-expiry">Expires {savedCard.expMonth}/{savedCard.expYear}</div>
        </div>
        <button 
          className="change-btn"
          onClick={() => setSavedCard(null)}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="card-form-wrapper">
      <div className="form-row">
        <div className="form-group full-width">
          <label>Card Number</label>
          <div className={`stripe-element-container ${focusedElement === 'cardNumber' ? 'focused' : ''} ${cardError ? 'error' : ''}`}>
            <CardNumberElement
              options={{ style: stripeElementStyle }}
              onFocus={() => setFocusedElement('cardNumber')}
              onBlur={() => setFocusedElement(null)}
            />
          </div>
        </div>
      </div>

      <div className="form-row three-col">
        <div className="form-group">
          <label>Expiration</label>
          <div className={`stripe-element-container ${focusedElement === 'cardExpiry' ? 'focused' : ''}`}>
            <CardExpiryElement
              options={{ style: stripeElementStyle }}
              onFocus={() => setFocusedElement('cardExpiry')}
              onBlur={() => setFocusedElement(null)}
            />
          </div>
        </div>
        <div className="form-group">
          <label>CVV</label>
          <div className={`stripe-element-container ${focusedElement === 'cardCvc' ? 'focused' : ''}`}>
            <CardCvcElement
              options={{ style: stripeElementStyle }}
              onFocus={() => setFocusedElement('cardCvc')}
              onBlur={() => setFocusedElement(null)}
            />
          </div>
        </div>
        <div className="form-group">
          <label>Zip / Postal Code</label>
          <input
            type="text"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            placeholder="12345"
            maxLength={10}
          />
        </div>
      </div>

      {cardError && <div className="card-errors">{cardError}</div>}

      <div className="section-actions">
        <button 
          className="update-btn" 
          onClick={handleSubmit}
          disabled={!stripe || processing}
        >
          {processing ? 'Processing...' : 'Update Payment Method'}
        </button>
      </div>
    </div>
  );
}

// Main Component
function PaymentSettings() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // Section expand states
  const [expandedSections, setExpandedSections] = useState({
    billingEmail: true,
    paymentMethod: true,
    billingAddress: false,
    discount: false,
    videoCredits: true
  });

  // Form data
  const [billingEmail, setBillingEmail] = useState('');
  const [savedCard, setSavedCard] = useState(null);
  const [zipCode, setZipCode] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    address: '',
    address2: '',
    city: '',
    state: '',
    country: 'United States',
    zip: '',
    phone: ''
  });
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  
  // FIXED: Track subscription discount separately (from signup)
  const [subscriptionDiscount, setSubscriptionDiscount] = useState(null);

  // Saving states
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [videoCredits, setVideoCredits] = useState(0);
  const [creditPurchases, setCreditPurchases] = useState([]);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);

  // US States list
  const usStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser?.uid) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setBillingEmail(data.billingEmail || data.email || currentUser.email || '');
            setZipCode(data.billingZip || '');
            
            if (data.billingAddress) {
              setBillingAddress(data.billingAddress);
            }
            
            if (data.savedCard) {
              setSavedCard(data.savedCard);
            }
            
            // Check for appliedCoupon (manually applied after signup)
            if (data.appliedCoupon) {
              setAppliedCoupon(data.appliedCoupon);
            }
            
            // FIXED: Also check subscription for discount applied at signup
            // This is the primary source of discount info for users who signed up with a coupon
            if (data.subscription?.discountCode && data.subscription?.discountPercent > 0) {
              setSubscriptionDiscount({
                code: data.subscription.discountCode,
                percentOff: data.subscription.discountPercent
              });
            }

            // Load video credits
            setVideoCredits(data.videoCredits ?? 0);

            // Load credit purchase history
            try {
              const purchasesRef = collection(db, 'users', currentUser.uid, 'creditPurchases');
              const purchasesQuery = query(purchasesRef, orderBy('purchasedAt', 'desc'));
              const purchasesSnap = await getDocs(purchasesQuery);
              const purchases = purchasesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
              setCreditPurchases(purchases);
            } catch (e) {
              console.error('Error loading purchase history:', e);
            }
          } else {
            setBillingEmail(currentUser.email || '');
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError('Failed to load payment settings');
        }
      }
      setLoading(false);
    };
    fetchUserData();
  }, [currentUser]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const showMessage = (msg) => {
    setMessage(msg);
    setError('');
    setTimeout(() => setMessage(''), 3000);
  };

  const showError = (err) => {
    setError(err);
    setMessage('');
    setTimeout(() => setError(''), 3000);
  };

  const handleUpdateEmail = async () => {
    setSavingEmail(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        billingEmail: billingEmail,
        updatedAt: new Date()
      });
      showMessage('Billing email updated successfully');
    } catch (err) {
      console.error('Error updating email:', err);
      showError('Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleUpdateAddress = async () => {
    setSavingAddress(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        billingAddress: billingAddress,
        updatedAt: new Date()
      });
      showMessage('Billing address updated successfully');
    } catch (err) {
      console.error('Error updating address:', err);
      showError('Failed to update address');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setApplyingCoupon(true);
    setError('');

    try {
      // This validates AND applies the coupon to the Stripe subscription + updates Firestore
      const result = await applyCouponToSubscription({
        userId: currentUser.uid,
        couponCode: couponCode.trim(),
      });
      const data = result.data;

      if (!data.success) {
        setError(data.error || 'Invalid coupon code');
        setApplyingCoupon(false);
        return;
      }

      const couponData = {
        code: data.code,
        percentOff: data.percentOff,
        amountOff: data.amountOff,
        appliedAt: new Date(),
      };

      setError('');
      setAppliedCoupon(couponData);
      setCouponCode('');
      showMessage(`Coupon applied: ${data.percentOff}% off!`);
    } catch (err) {
      console.error('Error applying coupon:', err);
      setError('Failed to apply coupon. Please try again.');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      const result = await removeCouponFromSubscription({
        userId: currentUser.uid,
      });
      const data = result.data;

      if (!data.success) {
        showError(data.error || 'Failed to remove coupon');
        return;
      }

      setAppliedCoupon(null);
      setSubscriptionDiscount(null);
      showMessage('Coupon removed');
    } catch (err) {
      console.error('Error removing coupon:', err);
      showError('Failed to remove coupon');
    }
  };

  if (loading) {
    return (
      <div className="payment-settings-page">
        <div className="payment-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // FIXED: Determine which coupon to display
  // Priority: 1) appliedCoupon (manually added), 2) subscriptionDiscount (from signup)
  const displayCoupon = appliedCoupon || subscriptionDiscount;
  const isSubscriptionCoupon = !appliedCoupon && subscriptionDiscount;

  return (
    <div className="payment-settings-page">
      <div className="payment-container">
        <div className="payment-header">
          <h1>Payment Settings</h1>
          <p>Manage your billing and payment information</p>
        </div>

        {message && (
          <div className="payment-message success">
            {message}
          </div>
        )}

        {error && (
          <div className="payment-message error">
            {error}
          </div>
        )}

        {/* Billing Email Section */}
        <div className="payment-section">
          <div className="section-header" onClick={() => toggleSection('billingEmail')}>
            <h2>Billing Email</h2>
            <span className={`section-toggle ${expandedSections.billingEmail ? 'open' : ''}`}>⌄</span>
          </div>
          <div className={`section-content ${!expandedSections.billingEmail ? 'collapsed' : ''}`}>
            <div className="form-group full-width">
              <label>Email</label>
              <input
                type="email"
                value={billingEmail}
                onChange={(e) => setBillingEmail(e.target.value)}
                placeholder="billing@example.com"
              />
            </div>
            <div className="section-actions">
              <button 
                className="update-btn"
                onClick={handleUpdateEmail}
                disabled={savingEmail}
              >
                {savingEmail ? 'Saving...' : 'Update Email'}
              </button>
            </div>
          </div>
        </div>

        {/* Payment Method Section */}
        <div className="payment-section">
          <div className="section-header" onClick={() => toggleSection('paymentMethod')}>
            <h2>Billing Information</h2>
            <span className={`section-toggle ${expandedSections.paymentMethod ? 'open' : ''}`}>⌄</span>
          </div>
          <div className={`section-content ${!expandedSections.paymentMethod ? 'collapsed' : ''}`}>
            <Elements stripe={stripePromise}>
              <CardForm 
                onSuccess={showMessage} 
                savedCard={savedCard}
                setSavedCard={setSavedCard}
                zipCode={zipCode}
                setZipCode={setZipCode}
              />
            </Elements>
          </div>
        </div>

        {/* Billing Address Section */}
        <div className="payment-section">
          <div className="section-header" onClick={() => toggleSection('billingAddress')}>
            <h2>Billing Address</h2>
            <span className={`section-toggle ${expandedSections.billingAddress ? 'open' : ''}`}>⌄</span>
          </div>
          <div className={`section-content ${!expandedSections.billingAddress ? 'collapsed' : ''}`}>
            <div className="form-row">
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={billingAddress.address}
                  onChange={(e) => setBillingAddress({...billingAddress, address: e.target.value})}
                  placeholder="exp : 123 Main St, Houston, TX"
                />
              </div>
              <div className="form-group">
                <label>Address Line 2 (Optional)</label>
                <input
                  type="text"
                  value={billingAddress.address2}
                  onChange={(e) => setBillingAddress({...billingAddress, address2: e.target.value})}
                  placeholder="Apt, Suite, etc."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={billingAddress.city}
                  onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                  placeholder="City"
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <select
                  value={billingAddress.country}
                  onChange={(e) => setBillingAddress({...billingAddress, country: e.target.value})}
                >
                  <option value="United States">United States</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>State</label>
                <select
                  value={billingAddress.state}
                  onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                >
                  <option value="">Select State</option>
                  {usStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Zip / Postal Code</label>
                <input
                  type="text"
                  value={billingAddress.zip}
                  onChange={(e) => setBillingAddress({...billingAddress, zip: e.target.value})}
                  placeholder="12345"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Phone</label>
                <input
                  type="tel"
                  value={billingAddress.phone}
                  onChange={(e) => setBillingAddress({...billingAddress, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="section-actions">
              <button 
                className="update-btn"
                onClick={handleUpdateAddress}
                disabled={savingAddress}
              >
                {savingAddress ? 'Saving...' : 'Update Billing Address'}
              </button>
            </div>
          </div>
        </div>

        {/* Discount Section */}
        <div className="payment-section">
          <div className="section-header" onClick={() => toggleSection('discount')}>
            <h2>Discount</h2>
            <span className={`section-toggle ${expandedSections.discount ? 'open' : ''}`}>⌄</span>
          </div>
          <div className={`section-content ${!expandedSections.discount ? 'collapsed' : ''}`}>
            {/* FIXED: Show coupon from either appliedCoupon OR subscription.discountCode */}
            {displayCoupon ? (
              <div className="coupon-display">
                <span className="coupon-code">{displayCoupon.code}</span>
                <span className="coupon-discount">
                  {displayCoupon.percentOff ? `${displayCoupon.percentOff}% off` : 'Applied'}
                </span>
                {/* Only show remove button for manually applied coupons, not signup coupons */}
                {!isSubscriptionCoupon && (
                  <button className="remove-coupon-btn" onClick={handleRemoveCoupon}>
                    Remove
                  </button>
                )}
                {isSubscriptionCoupon && (
                  <span className="coupon-source">(Applied at signup)</span>
                )}
              </div>
            ) : (
              <div className="coupon-form-wrapper">
                <div className="form-group full-width">
                  <label>Coupon Code</label>
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="section-actions">
                  <button 
                    className="update-btn"
                    onClick={handleApplyCoupon}
                    disabled={applyingCoupon || !couponCode.trim()}
                  >
                    {applyingCoupon ? 'Validating...' : 'Apply Coupon'}
                  </button>
                </div>
                {/* Coupon error message */}
                {error && !displayCoupon && (
                  <div className="coupon-error">
                    {error}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Video Credits Section */}
        <div className="payment-section">
          <div className="section-header" onClick={() => toggleSection('videoCredits')}>
            <h2>Video Credits</h2>
            <span className={`section-toggle ${expandedSections.videoCredits ? 'open' : ''}`}>⌄</span>
          </div>
          <div className={`section-content ${!expandedSections.videoCredits ? 'collapsed' : ''}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Film size={20} color={videoCredits > 0 ? '#28a745' : '#999'} />
                <div>
                  <span style={{ fontSize: '28px', fontWeight: 700, color: videoCredits > 0 ? '#1a1a2e' : '#999' }}>{videoCredits}</span>
                  <span style={{ fontSize: '14px', color: '#666', marginLeft: '4px' }}>credits remaining</span>
                </div>
              </div>
              <button
                onClick={() => setShowBuyCreditsModal(true)}
                className="update-btn"
              >
                Buy Credits
              </button>
            </div>

            {creditPurchases.length > 0 ? (
              <div style={{ background: '#fafafa', borderRadius: '8px', padding: '14px 16px' }}>
                <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, marginBottom: '10px' }}>Purchase History</div>
                {creditPurchases.map((purchase) => (
                  <div key={purchase.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 0', borderBottom: '1px solid #eee',
                  }}>
                    <div>
                      <span style={{ fontSize: '13px', color: '#1a1a2e' }}>{purchase.credits} credits</span>
                      <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
                        {new Date(purchase.purchasedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#1a1a2e' }}>
                      ${purchase.amount?.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
                Purchase credits to generate branded exercise videos in the{' '}
                <a href="/video-builder" style={{ color: '#dc3545', textDecoration: 'none' }}>Video Builder</a>.
              </p>
            )}
          </div>
        </div>

        <BuyCreditsModal
          isOpen={showBuyCreditsModal}
          onClose={() => setShowBuyCreditsModal(false)}
          currentBalance={videoCredits}
          cardLast4={savedCard?.last4 || ''}
          onPurchaseComplete={(newBalance) => {
            setVideoCredits(newBalance);
            // Refresh purchase history
            const fetchPurchases = async () => {
              try {
                const purchasesRef = collection(db, 'users', currentUser.uid, 'creditPurchases');
                const purchasesQuery = query(purchasesRef, orderBy('purchasedAt', 'desc'));
                const purchasesSnap = await getDocs(purchasesQuery);
                setCreditPurchases(purchasesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
              } catch (e) { console.error('Error refreshing purchases:', e); }
            };
            fetchPurchases();
          }}
        />

      </div>
    </div>
  );
}

export default PaymentSettings;